import { Injectable, Injector } from "@angular/core";
import { format } from "date-fns";

import { ServiceAbs } from "./abstracts/service.abs";
import { ApiResult } from "../entities/apis/api-result";
import { User } from "../entities/apis/user";
import { QueryResult } from "../entities/systems/query-result";
import { Hospital } from "../entities/apis/hospital";
import { Pagination } from "../entities/systems/pagination";
import { Task } from "../entities/apis/task";
import { Spare } from "../entities/apis/spare";
import { CodeType } from "../entities/apis/code-type";
import { Overview } from "../entities/apis/overview";

@Injectable()
export class ApiService extends ServiceAbs {
  constructor(injector: Injector) {
    super(injector);
    this.urlPrefix = "/api";
  }

  private invoke<T>(url: string, method: string, data?: any) {
    return new Promise<T>((resolve, reject) => {
      this.request<ApiResult<T>>(url, method, data)
        .then((r) => {
          if (r.Result === "0") {
            if (r.ReturnValue) {
              resolve(r.ReturnValue);
            } else if (r.Codes) {
              resolve(r.Codes);
            } else {
              resolve(<any>null);
            }
          } else {
            reject(r.Message);
          }
        })
        .catch((e) => {
          console.error(e);
          reject("接口请求失败。");
        });
    });
  }

  private invokeWithoutModel<T>(url: string, method: string, data?: any) {
    return new Promise<T>((resolve, reject) => {
      this.request<T>(url, method, data)
        .then((r) => {
          resolve(r);
        })
        .catch((e) => {
          console.error(e);
          reject("接口请求失败。");
        });
    });
  }

  login(data: any) {
    let url =
      this.urlPrefix +
      "/api-account/check-login-account?" +
      this.buildSearchFormQuery(data);
    return this.invoke<User>(url, "POST");
  }

  changePassword(oldPassword: string, newPassword: string) {
    let url = this.urlPrefix + "/change-password";
    return this.put(url, {
      oldPassword: oldPassword,
      newPassword: newPassword,
    });
  }

  findUsers(data?: any, pagination?: Pagination) {
    let url =
      this.urlPrefix +
      "/api-account/admin-find?" +
      this.buildSearchFormQuery(data) +
      this.buildPaginationQuery(pagination);
    return this.invoke<QueryResult<User>>(url, "GET");
  }

  findUserById(id: string) {
    let url = this.urlPrefix + "/api-account/admin-find-by-id/" + id;
    return this.invoke<User>(url, "GET");
  }

  createUser(data?: any) {
    let url = this.urlPrefix + "/api-account/add-login-account";
    return this.invoke<QueryResult<User>>(url, "POST", data);
  }

  destroyUser(id: string) {
    let url = this.urlPrefix + "/api-account/del-login-account/" + id;
    return this.invoke<QueryResult<any>>(url, "DELETE");
  }

  updateUser(id: string, data: any) {
    let url = this.urlPrefix + "/api-account/update-login-account/" + id;
    return this.invoke<QueryResult<User>>(url, "PUT", data);
  }

  getRegistQrcode(id: string) {
    let url = this.urlPrefix + "/api-account/get-regist-qrcode?id=" + id;
    return this.invokeWithoutModel<any>(url, "GET");
  }

  findHospitals(data?: any, pagination?: Pagination) {
    let url =
      this.urlPrefix +
      "/api-system/get-hospital?" +
      this.buildPaginationQuery(pagination) +
      this.buildSearchFormQuery({
        ...data,
      });
    return this.invokeWithoutModel<QueryResult<Hospital>>(url, "GET");
  }

  findTasks(data?: any, pagination?: Pagination) {
    let url =
      this.urlPrefix +
      "/api-fault/admin-query?" +
      this.buildPaginationQuery(pagination) +
      this.buildSearchFormQuery({
        ...(data.submitAt
          ? {
              submitFrom: format(data.submitAt[0], "yyyy-MM-dd 00:00:00"),
              submitTo: format(data.submitAt[1], "yyyy-MM-dd 23:59:59"),
            }
          : undefined),
        ...data,
        ...{
          submitAt: undefined,
        },
      });
    return this.invoke<QueryResult<Task>>(url, "GET");
  }

  exportTask(data?: any) {
    let url =
      this.urlPrefix +
      "/api-fault/admin-query-export?" +
      this.buildSearchFormQuery({
        ...(data.submitAt
          ? {
              submitFrom: format(data.submitAt[0], "yyyy-MM-dd 00:00:00"),
              submitTo: format(data.submitAt[1], "yyyy-MM-dd 23:59:59"),
            }
          : undefined),
        ...data,
        ...{
          submitAt: undefined,
        },
      });

    return this.download(url, "GET");
  }

  reassign(data: any) {
    let url = this.urlPrefix + "/api-fault/add-fault-process-action";
    return this.invoke<QueryResult<Task>>(url, "POST", data);
  }

  getCodes(category: string, include = true) {
    let url = this.urlPrefix + "/api-system/get-codes";
    return this.invoke<CodeType[]>(url, "POST", {
      codeTypeName: category,
      includeSubItem: include ? 1 : 0,
    });
  }

  findSpare(data?: any, pagination?: Pagination) {
    let url =
      this.urlPrefix +
      "/api-fault/get-ffault-spare-apply-info?" +
      this.buildPaginationQuery(pagination) +
      this.buildSearchFormQuery({
        ...(data.submitAt
          ? {
              create_at_from: data.submitAt[0],
              create_at_to: data.submitAt[1],
            }
          : undefined),
        ...data,
        ...{
          submitAt: undefined,
        },
      });
    return this.invoke<QueryResult<Spare>>(url, "GET");
  }

  exportSpare(data?: any, pagination?: Pagination) {
    let url =
      this.urlPrefix +
      "/api-fault/get-ffault-spare-apply-info?" +
      this.buildPaginationQuery(pagination) +
      this.buildSearchFormQuery({
        ...(data.submitAt
          ? {
              create_at_from: data.submitAt[0],
              create_at_to: data.submitAt[1],
            }
          : undefined),
        ...data,
        ...{
          submitAt: undefined,
        },
      });
    return this.download(url, "GET");
  }

  updateSpareInfo(data: any) {
    let url =
      this.urlPrefix + "/api-fault/update-fault-spare-apply-info/" + data.id;
    return this.invoke<QueryResult<Spare>>(url, "POST", data);
  }

  updateHospital(data: any) {
    let url = this.urlPrefix + "/api-system/update-hospital/" + data.id;
    return this.invoke<QueryResult<Hospital>>(url, "PUT", data);
  }

  createHospital(data: any) {
    let url = this.urlPrefix + "/api-system/add-hospital";
    return this.invoke<QueryResult<Hospital>>(url, "post", data);
  }

  RemoveHospital(data: any) {
    let url = this.urlPrefix + "/api-system/del-hospital/" + data.id;
    return this.invoke<QueryResult<Hospital>>(url, "DELETE", data);
  }

  QueryDashboardOverView(data: any) {
    let url =
      this.urlPrefix +
      "/api-fault/dashboard-overview?" +
      this.buildSearchFormQuery(data);
    return this.invokeWithoutModel<Overview>(url, "GET");
  }

  QueryDashboardRanking(data: any) {
    let url =
      this.urlPrefix +
      "/api-fault/dashboard-ranking?" +
      this.buildSearchFormQuery(data);
    return this.invokeWithoutModel<any>(url, "GET");
  }
  QueryDashboardTimeout(pagination?: Pagination) {
    let url =
      this.urlPrefix +
      "/api-fault/dashboard-timeout?" +
      this.buildPaginationQuery(pagination);
    return this.invokeWithoutModel<any>(url, "GET");
  }
  TimeOutRemind(data: any) {
    let url = this.urlPrefix + "/api-fault/dashboard-remind";
    return this.invokeWithoutModel<any>(url, "POST", data);
  }
  ApplyFaultInfo(data: any) {
    let url = this.urlPrefix + "/api-fault/add-fault-info";
    return this.invokeWithoutModel<any>(url, "POST", data);
  }
  UpdateFaultInfo(id: string, data: any) {
    let url = this.urlPrefix + "/api-fault/update-fault-info/" + id;
    return this.invokeWithoutModel<any>(url, "POST", data);
  }
  UpdateFaultHardward(data: any) {
    let url = this.urlPrefix + "/api-fault/add-fault-hardward";
    return this.invokeWithoutModel<any>(url, "POST", data);
  }
  DelFaultHardward(id: string) {
    let url = this.urlPrefix + "/api-fault/del-fault-hardward/" + id;
    return this.invokeWithoutModel<any>(url, "DELETE");
  }
  getCode(data?: any) {
    let url =
      this.urlPrefix +
      "/api-system/get-code?" +
      this.buildSearchFormQuery(data);
    return this.invokeWithoutModel<any>(url, "GET");
  }
}
